import { AxiosInstance, AxiosError } from 'axios';
import { IStorageProvider } from '../storage/IStorageProvider';
import { STORAGE_KEYS } from '../constants/storage';
import { ERROR_CODES } from '../constants/errors';
import { AuthRefreshError } from '../errors/AuthRefreshError';
import { TokenHelper } from '../utils/tokenHelper';

interface QueueItem {
  resolve: (value: string | null) => void;
  reject: (reason?: any) => void;
}

export class CoreInterceptors {
  private isRefreshing = false;
  private failedQueue: QueueItem[] = [];

  constructor(
    private readonly storage: IStorageProvider,
    private readonly refreshTokenCallback: () => Promise<string>,
    private readonly errorHandler?: (err: any) => void
  ) {}

  private processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  attach(axiosInstance: AxiosInstance) {
    axiosInstance.interceptors.request.use((config: any) => {
      const token = this.storage.get(STORAGE_KEYS.ACCESS_TOKEN);
      if (token) {
        if (!config.headers) config.headers = {};
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        if (config.headers?.Authorization) {
          delete config.headers.Authorization;
        }
      }
      return config;
    });

    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest: any = error.config;
        if (!originalRequest) return Promise.reject(error);

        const responseData: any = error.response?.data;
        const status = error.response?.status;
        const errorCode = responseData?.errorCode || responseData?.detail?.errorCode;

        if (status === 401 || errorCode === ERROR_CODES.TOKEN_EXPIRED) {
          if (!originalRequest._retry) {
            if (this.isRefreshing) {
              originalRequest._retry = true;
              return new Promise<string | null>((resolve, reject) => {
                this.failedQueue.push({ resolve, reject });
              })
                .then(token => {
                  if (token) {
                    originalRequest.headers['Authorization'] = 'Bearer ' + token;
                  }
                  return axiosInstance(originalRequest);
                })
                .catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            this.isRefreshing = true;

            try {
              const newToken = await this.refreshTokenCallback();
              axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
              originalRequest.headers['Authorization'] = 'Bearer ' + newToken;

              this.processQueue(null, newToken);
              return axiosInstance(originalRequest);
            } catch (refreshError) {
              // Clear access token and all authentication state from storage
              TokenHelper.removeTokens(this.storage);

              const customError = new AuthRefreshError(
                'Failed to refresh access token: Session expired or invalid.',
                refreshError
              );

              this.processQueue(customError, null);
              if (this.errorHandler) {
                this.errorHandler(customError);
              }
              return Promise.reject(customError);
            } finally {
              this.isRefreshing = false;
            }
          } else {
            // If it HAS been retried and still fails with 401, the new token is invalid
            TokenHelper.removeTokens(this.storage);

            const customError = new AuthRefreshError(
              'Retried request failed with 401: Session is invalid.',
              error
            );

            if (this.errorHandler) {
              this.errorHandler(customError);
            }
            return Promise.reject(customError);
          }
        }

        return Promise.reject(error);
      }
    );
  }
}
